import { useState } from "react";
import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import type { StreamMessage } from "../../hooks/useStreamingText";
import { pluralize } from "../../lib/inflection";
import { Icon } from "../Icon";
import { PulsingIcon } from "../PulsingIcon";
import { ShimmerSpan } from "../ShimmerSpan";
import { Text } from "../Text";
import { DefaultIcon, HoverIcon, ToggleIconContainer } from "../ToggleIcon";
import { WavyLine } from "../WavyLine";
import { TypewriterSpan } from "../TypewriterSpan";
import { ActivityRow } from "./ActivityRow";
import {
  CollapsibleHeader,
  CollapsibleHeaderInner,
  ExpandedContent,
  ExpandedContentHover,
  ExpandedTimestamp,
  ExpandInner,
  ExpandWrapper,
} from "./Expand";
import { TypewriterText } from "./TypewriterText";

export function Reasoning({
  entry,
  live,
  hasNext,
  stream,
  connected = !live,
  onExpandHistory,
}: {
  entry: ChatEntryWithSubTables;
  live: boolean;
  hasNext: boolean;
  stream: StreamMessage | undefined;
  connected?: boolean;
  onExpandHistory?: () => void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const expanded = !onExpandHistory && expandedId === entry.id;
  const reasoning = entry.assistantReasoning!;
  const seconds = Math.max(1, Math.round((reasoning.durationMs ?? 0) / 1000));
  const header = live
    ? "Thinking…"
    : reasoning.durationMs != null
      ? `Thought for ${seconds} ${pluralize("second", seconds)}`
      : "Thought";
  const text = stream?.text || reasoning.text;

  return (
    <ActivityRow connected={connected} hasNext={hasNext}>
      <CollapsibleHeader
        aria-expanded={expanded}
        onPress={
          onExpandHistory ?? (() => setExpandedId(expanded ? null : entry.id))
        }
      >
        <CollapsibleHeaderInner>
          {live ? (
            <PulsingIcon data-pending>
              <WavyLine size={12} color="var(--text-secondary)" opacity={1} />
            </PulsingIcon>
          ) : (
            <ToggleIconContainer aria-hidden>
              <DefaultIcon>
                <Icon name="brain" size={12} color="var(--text-secondary)" />
              </DefaultIcon>
              <HoverIcon>
                <Icon
                  name={expanded ? "minus" : "plus"}
                  size={12}
                  color="var(--text-secondary)"
                />
              </HoverIcon>
            </ToggleIconContainer>
          )}
          <Text variant="text-small" color="var(--text-secondary)">
            <ShimmerSpan data-pending={live}>
              <TypewriterSpan>{header}</TypewriterSpan>
            </ShimmerSpan>
          </Text>
        </CollapsibleHeaderInner>
      </CollapsibleHeader>
      {!onExpandHistory && text && (
        <ExpandWrapper data-expanded={expanded}>
          <ExpandInner data-expanded={expanded} inert={!expanded}>
            <ExpandedContentHover>
              <ExpandedContent>
                <TypewriterText
                  text={text}
                  streaming={live && stream?.status === "streaming"}
                  plain
                  variant="text-small"
                  color="var(--text-secondary)"
                />
              </ExpandedContent>
              <ExpandedTimestamp
                createdAt={entry.createdAt}
                durationMs={reasoning.durationMs}
              />
            </ExpandedContentHover>
          </ExpandInner>
        </ExpandWrapper>
      )}
    </ActivityRow>
  );
}
